// 🔍 Paleta de comandos (Ctrl/⌘ + K) — buscador global de opciones del menú.
//
// POR QUÉ EXISTE: el menú del perfil llega a tener ~400 opciones repartidas en
// más de 20 módulos. Encontrar una exige saber de antemano en qué submenú la
// guardaron, y tras la reorganización de los menús mucha gente ya no lo sabe.
// Además el Header llevaba tiempo mostrando un `⌘K` que no hacía nada.
//
// DE DÓNDE SALEN LOS DATOS: del mismo `useMenus()` que pinta el sidebar, que ya
// viene filtrado por las asignaciones del perfil y cacheado. No hay RPC nueva ni
// índice que mantener, y —lo importante— la paleta NO PUEDE enseñar una opción
// a la que el usuario no tenga acceso, porque no la tiene en el árbol.
//
// LA MIGA DE PAN NO ES ADORNO: hay 14 opciones llamadas «Ayuda» y 9 «Dashboard».
// Sin el «Módulo › Submenú» en cada fila, buscar «ayuda» daría catorce filas
// idénticas y la paleta sería inútil justo donde más falta hace.
import { useEffect, useMemo, useRef, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { ChevronRight, CornerDownLeft, Search, type LucideIcon } from 'lucide-react';
import { useMenus } from '@/lib/hooks/useMenus';
import { useTabsStore } from '@/stores/tabs.store';
import { usePaletaStore } from '@/stores/paleta.store';
import { MAX_RESULTADOS, aplanar, buscar, norm, type Comando } from './paletaBuscar';

const MAX_RECIENTES = 8;

const escaparRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function Resaltado({ texto, q }: { texto: string; q: string }) {
  const terminos = norm(q).split(/\s+/).filter(Boolean);
  if (!terminos.length) return <>{texto}</>;
  // Se busca sobre el texto normalizado pero se pintan los caracteres
  // ORIGINALES, para no perder tildes ni mayúsculas al resaltar.
  const plano = norm(texto);
  const marcas = new Array<boolean>(texto.length).fill(false);
  for (const t of terminos) {
    const re = new RegExp(escaparRegex(t), 'g');
    let m: RegExpExecArray | null;
    while ((m = re.exec(plano)) !== null) {
      for (let i = m.index; i < m.index + t.length && i < marcas.length; i++) marcas[i] = true;
      if (m.index === re.lastIndex) re.lastIndex++;
    }
  }
  const trozos: { txt: string; on: boolean }[] = [];
  for (let i = 0; i < texto.length; i++) {
    const ult = trozos[trozos.length - 1];
    if (ult && ult.on === marcas[i]) ult.txt += texto[i];
    else trozos.push({ txt: texto[i], on: marcas[i] });
  }
  return <>{trozos.map((t, i) => (t.on
    ? <mark key={i} className="bg-primary/20 text-foreground rounded-[2px] px-0">{t.txt}</mark>
    : <span key={i}>{t.txt}</span>))}</>;
}

const iconoDe = (nombre: string | null): LucideIcon | null =>
  (nombre ? (LucideIcons as unknown as Record<string, LucideIcon>)[nombre] : null) ?? null;

// Los recientes se guardan POR EMPRESA y, además, se cruzan contra el árbol de
// menús vivo al mostrarlos: una ruta que el perfil actual ya no tiene sale de la
// lista sola. Así un cambio de empresa o de permisos no puede filtrar una
// opción ajena por la puerta de atrás.
const claveRecientes = (empresaId: string) => `erp_paleta_recientes:${empresaId}`;

function leerRecientes(empresaId: string): string[] {
  if (!empresaId) return [];
  try {
    const raw = localStorage.getItem(claveRecientes(empresaId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch { return []; }
}

function anotarReciente(empresaId: string, ruta: string) {
  if (!empresaId) return;
  try {
    const prev = leerRecientes(empresaId).filter((r) => r !== ruta);
    localStorage.setItem(claveRecientes(empresaId), JSON.stringify([ruta, ...prev].slice(0, MAX_RECIENTES)));
  } catch { /* sin localStorage (modo privado): la paleta sigue funcionando sin recientes */ }
}

export function PaletaComandos() {
  const abierta = usePaletaStore((s) => s.abierta);
  const cerrar = usePaletaStore((s) => s.cerrar);
  const alternar = usePaletaStore((s) => s.alternar);
  const { data } = useMenus();
  const openTab = useTabsStore((s) => s.openTab);

  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [recientes, setRecientes] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const filasRef = useRef<(HTMLButtonElement | null)[]>([]);

  const comandos = useMemo(() => aplanar(data?.menus), [data]);
  const empresaId = data?.empresa?.id ?? '';
  const porRuta = useMemo(() => new Map(comandos.map((c) => [c.ruta, c])), [comandos]);

  // Con la caja vacía se ofrecen los recientes; es lo que el usuario va a querer
  // el 80 % de las veces y evita que la paleta abra en blanco.
  const resultados = useMemo(() => {
    if (q.trim()) return buscar(comandos, q);
    return recientes.map((r) => porRuta.get(r)).filter((c): c is Comando => !!c);
  }, [comandos, q, recientes, porRuta]);
  const mostrandoRecientes = !q.trim() && resultados.length > 0;

  // El atajo global. Se registra siempre (aunque la paleta esté cerrada) y hay
  // que frenar el evento: Ctrl+K es el atajo de la barra de direcciones en
  // varios navegadores y, sin preventDefault, se lo quedan ellos.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        alternar();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [alternar]);

  // Cada apertura empieza limpia, con el foco puesto y los recientes frescos.
  useEffect(() => {
    if (!abierta) return;
    setQ(''); setSel(0);
    setRecientes(leerRecientes(empresaId));
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [abierta, empresaId]);

  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => { filasRef.current[sel]?.scrollIntoView({ block: 'nearest' }); }, [sel, resultados]);

  if (!abierta) return null;

  const abrirOpcion = (c: Comando) => {
    anotarReciente(empresaId, c.ruta);
    openTab({ id: c.ruta, title: c.nombre, path: c.ruta, icon: c.icon || undefined });
    cerrar();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); cerrar(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => (resultados.length ? (s + 1) % resultados.length : 0)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => (resultados.length ? (s - 1 + resultados.length) % resultados.length : 0)); return; }
    if (e.key === 'Home') { e.preventDefault(); setSel(0); return; }
    if (e.key === 'End') { e.preventDefault(); setSel(Math.max(0, resultados.length - 1)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const c = resultados[sel];
      if (c) abrirOpcion(c);
    }
  };

  // Desplegable de búsqueda (sin fondo oscuro ni desenfoque): la página sigue a la
  // vista; un clic afuera o Esc lo cierra.
  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-[12vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) cerrar(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Buscar una opción del menú"
        className="w-full max-w-2xl overflow-hidden rounded-xl border bg-card shadow-2xl"
      >
        <div className="flex items-center gap-3 border-b px-4">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar una opción…  (por ejemplo: retenciones, kardex, rol de pagos)"
            aria-label="Buscar una opción del menú"
            aria-autocomplete="list"
            className="h-14 w-full bg-transparent text-base outline-none placeholder:text-muted-foreground"
          />
          <kbd className="hidden shrink-0 rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">esc</kbd>
        </div>

        <div role="listbox" aria-label="Resultados" className="max-h-[52vh] overflow-y-auto p-2">
          {mostrandoRecientes && (
            <p className="px-2 pb-1 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Recientes</p>
          )}

          {resultados.map((c, i) => {
            const Icono = iconoDe(c.icon);
            return (
              <button
                key={c.ruta}
                ref={(el) => { filasRef.current[i] = el; }}
                role="option"
                aria-selected={i === sel}
                onMouseMove={() => setSel(i)}
                onClick={() => abrirOpcion(c)}
                className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors ${
                  i === sel ? 'bg-accent' : 'hover:bg-accent/50'}`}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-background">
                  {Icono ? <Icono className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-foreground">
                    <Resaltado texto={c.nombre} q={q} />
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {c.menu} <span aria-hidden="true">›</span> {c.submenu}
                  </span>
                </span>
                {i === sel && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
              </button>
            );
          })}

          {!resultados.length && (
            <p className="px-2 py-8 text-center text-sm text-muted-foreground">
              {q.trim()
                ? <>Ninguna opción coincide con «{q.trim()}».</>
                : <>Escribe para buscar entre {comandos.length} opciones de tu menú.</>}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t bg-muted/40 px-4 py-2 text-[11px] text-muted-foreground">
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span><kbd className="rounded border bg-background px-1 font-mono">↑</kbd> <kbd className="rounded border bg-background px-1 font-mono">↓</kbd> moverse</span>
            <span><kbd className="rounded border bg-background px-1 font-mono">↵</kbd> abrir</span>
            <span><kbd className="rounded border bg-background px-1 font-mono">esc</kbd> cerrar</span>
          </span>
          <span className="shrink-0">
            {q.trim()
              ? `${resultados.length}${resultados.length === MAX_RESULTADOS ? '+' : ''} de ${comandos.length}`
              : `${comandos.length} opciones`}
          </span>
        </div>
      </div>
    </div>
  );
}
